/* eslint-disable @typescript-eslint/no-explicit-any */
import { Card, CardContent } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  CheckCircle2,
  ChevronDown,
  ChevronDownIcon,
  ChevronUpIcon,
  Info,
  Loader2,
  XCircle,
} from "lucide-react";

import MoreInfoButton from "@/components/more-info-button";
import StyledTooltip from "@/components/styled-tooltip";
import { Button } from "@/components/ui/button";
import { getTooltip } from "@/pages/email-health/email-health-tooltips";
import { useEmailHealthReport } from "./email-health-context";
import { getWhoisData } from "./email-health-domain-info";
import { cn } from "@/lib/utils";
import { EmailHealthRecord } from "./email-health-context";
import { DnsRecordsResponse } from "@/types/postman.types";

const RecordDisplay = ({
  record,
  recordsData,
  isPrinting,
}: {
  record: EmailHealthRecord;
  recordsData: DnsRecordsResponse;
  isPrinting: boolean;
}) => {
  const renderSubRecords = () => {
    switch (record.type) {
      case "DNSSEC":
        return (
          <div className="bg-slate-50 dark:bg-secondary/50 p-4 rounded-lg">
            <div className="text-sm text-slate-600 dark:text-slate-300 space-y-3">
              <div className="pb-2 border-b">
                <div className="font-medium mb-1">Domain DNSSEC</div>
                <div className="space-y-1 pl-2">
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        recordsData?.domainReport?.mailServerDnssec?.isValid
                          ? "bg-green-500"
                          : "bg-red-500"
                      }`}
                    />
                    <EntryItem
                      label="Domain"
                      value={
                        recordsData?.domainReport?.mailServerDnssec?.domain ||
                        record.record?.domain
                      }
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        recordsData?.domainReport?.mailServerDnssec
                          ?.isDnssecSigned
                          ? "bg-green-500"
                          : "bg-red-500"
                      }`}
                    />
                    <EntryItem
                      label="Domain Registrar"
                      value={
                        recordsData?.domainReport?.mailServerDnssec?.registrar
                      }
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        recordsData?.domainReport?.mailServerDnssec
                          ?.isDnssecSigned
                          ? "bg-green-500"
                          : "bg-red-500"
                      }`}
                    />

                    <EntryItem
                      label="DNSSEC Signed"
                      value={
                        recordsData?.domainReport?.mailServerDnssec
                          ?.isDnssecSigned ? (
                          <span className="text-green-600">Yes</span>
                        ) : (
                          <span className="text-red-600">No</span>
                        )
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="pb-2">
                <div className="font-medium mb-1">Mail Server DNSSEC</div>
                <div className="space-y-1 pl-2">
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        (recordsData as any)?.mailServerReports[0]
                          ?.mailServerDnssec?.isValid
                          ? "bg-green-500"
                          : "bg-red-500"
                      }`}
                    />

                    <EntryItem
                      label="Mail Server"
                      value={
                        (recordsData as any)?.mailServerReports[0]
                          ?.mailServerDnssec?.domain
                      }
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        (recordsData as any)?.mailServerReports[0]
                          ?.mailServerDnssec?.isDnssecSigned
                          ? "bg-green-500"
                          : "bg-red-500"
                      }`}
                    />
                    <EntryItem
                      label="DNSSEC Signed"
                      value={
                        (recordsData as any)?.mailServerReports[0]
                          ?.mailServerDnssec?.isDnssecSigned ? (
                          <span className="text-green-600">Yes</span>
                        ) : (
                          <span className="text-red-600">No</span>
                        )
                      }
                    />
                  </div>
                  {(recordsData as any)?.mailServerReports[0]?.mailServerDnssec
                    ?.registrar && (
                    <div className="flex items-center gap-2">
                      <span className="">
                        Registrar:{" "}
                        {
                          (recordsData as any)?.mailServerReports[0]
                            ?.mailServerDnssec?.registrar
                        }
                      </span>
                    </div>
                  )}
                  {(recordsData as any)?.mailServerReports[0]?.mailServerDnssec
                    ?.errors &&
                    (recordsData as any)?.mailServerReports[0]?.mailServerDnssec
                      ?.errors?.length > 0 && (
                      <div className="mt-2">
                        <div className="font-medium text-red-600">Errors:</div>
                        <ul className="list-disc pl-5 text-red-600">
                          {(
                            recordsData as any
                          )?.mailServerReports[0]?.mailServerDnssec?.errors?.map(
                            (error: string, idx: number) => (
                              <li key={idx}>{error}</li>
                            )
                          )}
                        </ul>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        );

      case "DMARC":
        return (
          <>
            <div className="space-y-4">
              <p className="text-slate-600 dark:text-slate-300">
                {record.record?.record?.status?.comment ||
                  "DMARC status not available"}
              </p>

              <div className="bg-slate-50 dark:bg-secondary/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      record.record?.result ? "bg-green-500" : "bg-red-500"
                    }`}
                  />
                  <span className="font-medium">DMARC Policy</span>
                </div>

                <div className="space-y-2">
                  {record.record?.record?.rr &&
                    record.record.record.rr
                      .split(";")
                      .map((policy: string, idx: number) => {
                        const [key, value] = policy.trim().split("=");
                        if (!key || !value) return null;
                        return (
                          <div
                            key={idx}
                            className="flex items-start gap-2 text-sm"
                          >
                            <span className=" text-slate-500 dark:text-slate-400">
                              {key}=
                            </span>
                            <span className=" text-slate-700 dark:text-slate-300">
                              {value}
                            </span>
                          </div>
                        );
                      })}
                </div>

                {record.record?.record?.alignment && (
                  <div className="mt-4">
                    <p className="font-medium mb-2">Alignment</p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-500 dark:text-slate-400">SPF:</span>
                        <span className="text-sm">
                          {record.record.record.alignment.spf?.result}
                        </span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          (Mode:{" "}
                          {record.record.record.alignment.spf.strict
                            ? "strict"
                            : "relaxed"}
                          )
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-500 dark:text-slate-400">DKIM:</span>
                        <span className="text-sm">
                          {record.record.record.alignment.dkim?.result}
                        </span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          (Mode:{" "}
                          {record.record.record.alignment.dkim.strict
                            ? "strict"
                            : "relaxed"}
                          )
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        );

      case "DKIM":
        return (
          <div className="space-y-4">
            <p className="text-slate-600 dark:text-slate-300">
              {record.record?.record?.[0]?.status?.result === "pass"
                ? `DKIM Signature verified: ${record.record.record[0].status?.result} (${record.record.record[0].signingDomain})`
                : "DKIM verification failed"}
            </p>

            {record.record?.record?.map((dkimRecord: any, index: number) => (
              <div key={index} className="bg-slate-50 dark:bg-secondary/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      dkimRecord.status?.result === "pass"
                        ? "bg-green-500"
                        : "bg-red-500"
                    }`}
                  />
                  <span className="font-medium">Signature {index + 1}</span>
                </div>

                <div className="space-y-2">
                  <EntryItem label="Domain" value={dkimRecord.signingDomain} />
                  <EntryItem label="Selector" value={dkimRecord.selector} />
                  <EntryItem label="Algorithm" value={dkimRecord.algo} />
                  <EntryItem label="Result" value={dkimRecord.status?.result} />
                </div>
              </div>
            ))}
          </div>
        );

      case "SPF":
        return (
          <div className="bg-slate-50 dark:bg-secondary/50 p-4 rounded-lg">
            <div className="space-y-1">
              <EntryItem label="Domain" value={record.record?.record?.domain} />
              <EntryItem
                label="Client IP"
                value={record.record?.record?.["client-ip"]}
              />
              <EntryItem label="HELO" value={record.record?.record?.helo} />
              <EntryItem
                label="Envelope From"
                value={record.record?.record?.["envelope-from"]}
              />
              <EntryItem label="Record" value={record.record?.record?.rr} />

              <EntryItem
                label="Result"
                value={record.record?.record?.status?.result}
              />
              <EntryItem
                label="Comment"
                value={record.record?.record?.status?.comment}
              />
            </div>
          </div>
        );

      case "TLS":
        return (
          <div className="bg-slate-50 dark:bg-secondary/50 p-4 rounded-lg">
            <div className="text-sm text-slate-600 dark:text-slate-300 space-y-3">
              {Array.isArray(record.record?.record) ? (
                record.record.record.map((tlsGroup: any, groupIdx: number) => (
                  <div key={groupIdx} className="pb-2 border-b last:border-0">
                    <div className="font-medium mb-1">
                      {typeof tlsGroup.at?.(-1) === "string"
                        ? tlsGroup.at(-1)
                        : `Connection ${groupIdx}`}
                    </div>
                    <div className="space-y-1 pl-2">
                      {Array.isArray(tlsGroup) &&
                        tlsGroup.map((tls: any, idx: number) =>
                          tls.version ? (
                            <div key={idx} className="flex items-center gap-2">
                              <div
                                className={`w-2 h-2 rounded-full ${
                                  tls.status === "good"
                                    ? "bg-green-500"
                                    : tls.status === "sufficient"
                                    ? "bg-yellow-500"
                                    : "bg-red-500"
                                }`}
                              />

                              <EntryItem
                                label={tls.version}
                                value={
                                  <span
                                    className={
                                      tls.status === "good"
                                        ? "text-green-600"
                                        : tls.status === "sufficient"
                                        ? "text-yellow-600"
                                        : "text-red-600"
                                    }
                                  >
                                    {tls.status}
                                  </span>
                                }
                              />
                            </div>
                          ) : null
                        )}
                    </div>
                  </div>
                ))
              ) : (
                <div>No TLS records found</div>
              )}
            </div>
          </div>
        );

      case "SSL":
        return (
          <div className="flex flex-col gap-4">
            <div>
              <h2> Mail Server</h2>
              <div className="mt-2 bg-slate-50 dark:bg-secondary/50 p-4 rounded-lg text-slate-600 dark:text-slate-300 space-y-1">
                <EntryItem
                  label="Trust Chain"
                  value={
                    record.record?.record?.trustChainValid ? (
                      <span className="text-green-600">Valid</span>
                    ) : (
                      <span className="text-red-600">Invalid</span>
                    )
                  }
                />
                <EntryItem
                  label="Public Key"
                  value={
                    record.record?.record?.publicKeyValid ? (
                      <span className="text-green-600">Valid</span>
                    ) : (
                      <span className="text-red-600">Invalid</span>
                    )
                  }
                />
                <EntryItem
                  label="Signature"
                  value={
                    record.record?.record?.signatureValid ? (
                      <span className="text-green-600">Valid</span>
                    ) : (
                      <span className="text-red-600">Invalid</span>
                    )
                  }
                />
                <EntryItem
                  label="Domain Match"
                  value={
                    record.record?.record?.domainMatch ? (
                      <span className="text-green-600">Valid</span>
                    ) : (
                      <span className="text-red-600">Invalid</span>
                    )
                  }
                />
              </div>
            </div>

            <div>
              <h2>Web Server</h2>
              <div className="mt-2 bg-slate-50 dark:bg-secondary/50 p-4 rounded-lg">
                {!recordsData?.domainReport ? (
                  <div>No SSL records found</div>
                ) : (
                  <div className="space-y-1">
                    <EntryItem
                      label="Trust Chain"
                      value={
                        recordsData?.domainReport?.sslCertificate
                          ?.trustChainValid ? (
                          <span className="text-green-600">Valid</span>
                        ) : (
                          <span className="text-red-600">Invalid</span>
                        )
                      }
                    />
                    <EntryItem
                      label="Public Key"
                      value={
                        recordsData?.domainReport?.sslCertificate
                          ?.publicKeyValid ? (
                          <span className="text-green-600">Valid</span>
                        ) : (
                          <span className="text-red-600">Invalid</span>
                        )
                      }
                    />
                    <EntryItem
                      label="Signature"
                      value={
                        recordsData?.domainReport?.sslCertificate
                          ?.signatureValid ? (
                          <span className="text-green-600">Valid</span>
                        ) : (
                          <span className="text-red-600">Invalid</span>
                        )
                      }
                    />
                    <EntryItem
                      label="Domain Match"
                      value={
                        recordsData?.domainReport?.sslCertificate
                          ?.domainMatch ? (
                          <span className="text-green-600">Valid</span>
                        ) : (
                          <span className="text-red-600">Invalid</span>
                        )
                      }
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case "MX":
        return (
          <div className="bg-slate-50 dark:bg-secondary/50 p-4 rounded-lg">
            <div className="text-sm  text-slate-600 dark:text-slate-300">
              {(() => {
                const mxRecords =
                  record.record?.record || record.record?.records;

                if (!mxRecords) {
                  return "No MX records found";
                }

                // Handle both array and single record cases
                const recordArray = Array.isArray(mxRecords)
                  ? mxRecords
                  : [mxRecords];

                return recordArray.map((mx: any, idx: number) => (
                  <div key={idx}>
                    {typeof mx === "string" ? mx : JSON.stringify(mx)}
                  </div>
                ));
              })()}
            </div>
          </div>
        );

      case "STARTTLS":
        return (
          <div className="bg-slate-50 dark:bg-secondary/50 p-4 rounded-lg">
            <div className="text-sm  text-slate-600 dark:text-slate-300 space-y-2">
              {(record.record?.records || record.record?.record)?.map(
                (item: any, idx: number) => (
                  <div key={idx} className="flex items-center gap-2">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        item[1] ? "bg-green-500" : "bg-red-500"
                      }`}
                    />
                    <span>
                      {item[0]}:{" "}
                      {item[1] ? (
                        <span className="text-green-600">Enabled</span>
                      ) : (
                        <span className="text-red-600">Disabled</span>
                      )}
                    </span>
                  </div>
                )
              )}
              {!record.record?.records?.length &&
                !record.record?.record?.length && (
                  <div>No STARTTLS records found</div>
                )}
            </div>
          </div>
        );

      case "ARC":
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-4">
              {record.record && (
                <>
                  <div className="flex flex-col items-center p-3 bg-slate-50 dark:bg-secondary/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className={`w-2 h-2 rounded-full shrink-0 ${
                          record.record.SPF ? "bg-green-500" : "bg-red-500"
                        }`}
                      />
                      <span className="font-medium">ARC SPF</span>
                    </div>
                    <span className="text-xs text-slate-700 dark:text-slate-300">
                      {record.record.SPF ? "Valid" : "Invalid"}
                    </span>
                  </div>
                  <div className="flex flex-col items-center p-3 bg-slate-50 dark:bg-secondary/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className={`w-2 h-2 rounded-full shrink-0 ${
                          record.record.DKIM ? "bg-green-500" : "bg-red-500"
                        }`}
                      />
                      <span className="font-medium">ARC DKIM</span>
                    </div>
                    <span className="text-xs text-slate-700 dark:text-slate-300 ">
                      {record.record.DKIM ? "Valid" : "Invalid"}
                    </span>
                  </div>
                  <div className="flex flex-col items-center p-3 bg-slate-50 dark:bg-secondary/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className={`w-2 h-2 rounded-full shrink-0 ${
                          record.record.DMARC ? "bg-green-500" : "bg-red-500"
                        }`}
                      />
                      <span className="font-medium">ARC DMARC</span>
                    </div>
                    <span className="text-xs text-slate-700 dark:text-slate-300 ">
                      {record.record.DMARC ? "Valid" : "Invalid"}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        );

      case "DNSKEY":
        return (
          <div className="bg-slate-50 dark:bg-secondary/50 p-4 rounded-lg">
            <div className="text-sm text-slate-600 dark:text-slate-300 space-y-3">
              {record.status === "invalid" ? (
                <div>
                  <p className="text-slate-600">No DNSKEY records found</p>
                </div>
              ) : (
                (record.record?.record || record.record?.records)?.map(
                  (dnskey: any, idx: number) => (
                    <div key={idx} className="border-b last:border-0 pb-2">
                      <div className="font-medium mb-1">Record {idx + 1}</div>
                      <div className="space-y-1">
                        <EntryItem label="Name" value={dnskey.name} />
                        <EntryItem
                          label="Type"
                          value={dnskey.type === 48 ? "DNSKEY" : "RRSIG"}
                        />
                        <EntryItem label="TTL" value={dnskey.TTL} />
                        <EntryItem
                          label="Data"
                          value={
                            dnskey.type === 48 ? (
                              <>
                                {dnskey.data
                                  .split(" ")
                                  .map((part: string, i: number) => (
                                    <span
                                      key={i}
                                      className={i <= 2 ? "text-blue-600" : ""}
                                    >
                                      {part}{" "}
                                    </span>
                                  ))}
                              </>
                            ) : (
                              dnskey.data
                            )
                          }
                        />

                        {dnskey.type === 48 && (
                          <div className="text-xs  text-slate-500 mt-1">
                            {parseInt(dnskey.data.split(" ")[0]) === 257
                              ? "Key Signing Key (KSK)"
                              : "Zone Signing Key (ZSK)"}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                )
              )}
            </div>
          </div>
        );

      case "RPKI":
        return (
          <div className="bg-slate-50 dark:bg-secondary/50 p-4 rounded-lg">
            {record.status === "invalid" ? (
              <div>
                <p className="text-slate-600 dark:text-slate-300">No RPKI records found</p>
              </div>
            ) : (
              (record.record?.records || record.record?.record)?.map(
                (rpki: any, idx: number) => (
                  <div key={idx} className="space-y-4">
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          rpki.hasValidROA ? "bg-green-500" : "bg-red-500"
                        }`}
                      />
                      <span className="font-medium">
                        ROA Status:{" "}
                        {rpki.hasValidROA ? (
                          <span className="text-green-600">Valid</span>
                        ) : (
                          <span className="text-red-600">Invalid</span>
                        )}
                      </span>
                    </div>

                    <div className="space-y-2">
                      <div className="font-medium">
                        Server: {rpki.checkedServer.server}
                      </div>
                      <div className="space-y-3">
                        {rpki.checkedServer.ipAddresses.map(
                          (ip: any, ipIdx: number) => (
                            <div
                              key={ipIdx}
                              className="bg-white dark:bg-secondary/50 p-3 rounded border border-slate-100 dark:border-slate-800"
                            >
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-slate-600 dark:text-slate-300">
                                <EntryItem label="Address" value={ip.address} />
                                <EntryItem
                                  label="BGP Prefix"
                                  value={ip.bgpPrefix}
                                />
                                <EntryItem label="ASN" value={ip.asn} />
                                <EntryItem
                                  label="Status"
                                  value={
                                    ip.validationState === "valid" ? (
                                      <span className="text-green-600">
                                        Valid
                                      </span>
                                    ) : (
                                      <span className="text-red-600">
                                        Invalid
                                      </span>
                                    )
                                  }
                                />
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>

                    {rpki.errors.length > 0 && (
                      <div className="mt-2">
                        <div className="font-medium text-red-600">Errors:</div>
                        <ul className="list-disc list-inside text-sm text-red-600 ">
                          {rpki.errors.map(
                            (error: string, errorIdx: number) => (
                              <li key={errorIdx}>{error}</li>
                            )
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                )
              )
            )}
          </div>
        );

      case "WHOIS":
        return (
          <div className="space-y-4">
            <p className="text-slate-600 dark:text-slate-300">
              {record?.status
                ? "WHOIS record found and verified"
                : "WHOIS verification failed"}
            </p>

            <div className="bg-slate-50 dark:bg-secondary/50 rounded-lg p-4">
              {/* Domain Information */}
              <div className="mb-5">
                <h3 className="text-sm font-semibold mb-3 text-slate-700 dark:text-slate-300 border-b pb-1">
                  Domain Information
                </h3>
                <div className="grid grid-cols-1 gap-y-2 gap-x-4 text-sm">
                  <EntryItem
                    label="Domain Name"
                    value={getWhoisData(record)?.["Domain Name"]}
                  />
                  <EntryItem
                    label="Registry ID"
                    value={getWhoisData(record)?.["Registry Domain ID"]}
                  />
                  <EntryItem
                    label="Created"
                    value={getWhoisData(record)?.["Created Date"]}
                  />
                  <EntryItem
                    label="Updated"
                    value={getWhoisData(record)?.["Updated Date"]}
                  />
                  <EntryItem
                    label="Expires"
                    value={getWhoisData(record)?.["Expiry Date"]}
                  />
                </div>
              </div>

              {/* Registrar Information */}
              <div className="mb-5">
                <h3 className="text-sm font-semibold mb-3 text-slate-700 dark:text-slate-300 border-b pb-1">
                  Registrar Information
                </h3>
                <div className="grid grid-cols-1 gap-y-2 gap-x-4 text-sm">
                  <EntryItem
                    label="Registrar"
                    value={getWhoisData(record)?.["Registrar"]}
                  />
                  <EntryItem
                    label="Registrar IANA ID"
                    value={getWhoisData(record)?.["Registrar IANA ID"]}
                  />
                  <EntryItem
                    label="Registrar URL"
                    value={getWhoisData(record)?.["Registrar URL"]}
                  />
                  <EntryItem
                    label="Abuse Email"
                    value={
                      getWhoisData(record)?.["Registrar Abuse Contact Email"]
                    }
                  />
                  <EntryItem
                    label="Abuse Phone"
                    value={
                      getWhoisData(record)?.["Registrar Abuse Contact Phone"]
                    }
                  />
                </div>
              </div>

              {/* Domain Status */}
              <div className="mb-5">
                <h3 className="text-sm font-semibold mb-3 text-slate-700 dark:text-slate-300 border-b pb-1">
                  Domain Status
                </h3>
                <div className="space-y-2">
                  {getWhoisData(record)?.["Domain Status"]?.map(
                    (status: string, idx: number) => {
                      const statusParts = status.split(" ");
                      const statusType = statusParts[0];
                      const statusUrl = statusParts[1] || "";
                      return (
                        <div key={idx} className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-blue-500" />
                            <span className="text-sm  text-slate-600 dark:text-slate-300">
                              {statusType}
                            </span>
                          </div>
                          {statusUrl && (
                            <span className="text-sm text-slate-700 dark:text-slate-300  break-all">
                              {statusUrl}
                            </span>
                          )}
                        </div>
                      );
                    }
                  )}
                </div>
              </div>

              {/* Name Servers */}
              <div className="mb-5">
                <h3 className="text-sm font-semibold mb-3 text-slate-700 dark:text-slate-300 border-b pb-1">
                  Name Servers
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {getWhoisData(record)?.["Name Server"]?.map(
                    (ns: string, idx: number) => (
                      <div
                        key={idx}
                        className="bg-white dark:bg-secondary/50 p-2 rounded border border-slate-200 dark:border-slate-800 text-sm "
                      >
                        {ns}
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* DNSSEC Information */}
              {getWhoisData(record)?.["DNSSEC"] && (
                <div className="mb-5">
                  <h3 className="text-sm font-semibold mb-3 text-slate-700 dark:text-slate-300 border-b pb-1">
                    DNSSEC
                  </h3>
                  <div className="space-y-2 text-sm">
                    <EntryItem
                      label="Status"
                      value={getWhoisData(record)?.["DNSSEC"]}
                    />

                    {getWhoisData(record)?.["DNSSEC DS Data"] && (
                      <EntryItem
                        label="DS Data"
                        value={getWhoisData(record)?.["DNSSEC DS Data"]}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Additional Information */}
              {getWhoisData(record)?.text && (
                <div>
                  <h3 className="text-sm font-semibold mb-3 text-slate-700 dark:text-slate-300 border-b pb-1">
                    Additional Information
                  </h3>
                  <div
                    className={cn(
                      "space-y-1 text-xs text-slate-600 dark:text-slate-300 bg-white dark:bg-secondary/50 p-3 rounded border border-slate-200 dark:border-slate-800",
                      !isPrinting && "max-h-40 overflow-y-auto"
                    )}
                  >
                    {getWhoisData(record)?.text.map(
                      (line: string, idx: number) => (
                        <p key={idx}>{line}</p>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      case "PTR": {
        // Use the actual record instead of sample data

        return (
          <div className="space-y-4">
            <p className="text-slate-600 dark:text-slate-300">
              {record?.record?.result
                ? "PTR records properly configured"
                : "PTR record configuration issues detected"}
            </p>

            {/* PTR Record */}
            <div className="bg-slate-50 dark:bg-secondary/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <div
                  className={`w-2 h-2 rounded-full ${
                    record?.record?.result ? "bg-green-500" : "bg-red-500"
                  }`}
                />
                <span className="font-medium">Inbound PTR Record</span>
              </div>

              <div className="space-y-3">
                {record.record ? (
                  <div className="bg-white dark:bg-secondary/50 p-3 rounded border border-slate-100 dark:border-slate-800">
                    <EntryItem
                      label="IP Address"
                      value={
                        typeof (
                          record.record.record || record.record.records
                        ) === "string"
                          ? record.record.record || record.record.records
                          : Array.isArray(
                              record.record.record || record.record.records
                            ) &&
                            (record.record.record || record.record.records)
                              .length > 0
                          ? (record.record.record || record.record.records).map(
                              (item: string) => item
                            )
                          : "N/A"
                      }
                    />

                    <EntryItem
                      label="Status"
                      value={
                        record.record?.result ? (
                          <span className="text-green-600">Valid</span>
                        ) : (
                          <span className="text-red-600">Invalid</span>
                        )
                      }
                    />
                  </div>
                ) : (
                  <div className="text-sm  text-slate-600 dark:text-slate-300">
                    No PTR records found
                  </div>
                )}
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-secondary/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <div
                  className={`w-2 h-2 rounded-full ${
                    record?.record?.result ? "bg-green-500" : "bg-red-500"
                  }`}
                />
                <span className="font-medium">Outbound PTR Record</span>
              </div>

              <div className="space-y-3">
                {recordsData?.outboundPTR ? (
                  <div className="bg-white dark:bg-secondary/50 p-3 rounded border border-slate-100 dark:border-slate-800">
                    <EntryItem
                      label="IP Address"
                      value={
                        typeof recordsData?.outboundPTR === "string"
                          ? recordsData?.outboundPTR
                          : Array.isArray(recordsData?.outboundPTR) &&
                            recordsData.outboundPTR.length > 0
                          ? recordsData.outboundPTR
                              .map((item: string) => item)
                              .join(", ")
                          : "N/A"
                      }
                    />

                    <EntryItem
                      label="Status"
                      value={
                        recordsData?.outboundPTR ? (
                          <span className="text-green-600">Valid</span>
                        ) : (
                          <span className="text-red-600">Invalid</span>
                        )
                      }
                    />
                  </div>
                ) : (
                  <div className="text-sm  text-slate-600 dark:text-slate-300">
                    No PTR records found
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      }
      case "BIMI": {
        const bimiData = record.record?.record || record.record?.records;
        let logoUrl = "";

        // Add null check before processing BIMI data
        if (bimiData) {
          if (Array.isArray(bimiData)) {
            for (const item of bimiData) {
              if (typeof item === "string") {
                const match = item.match(/l=([^;]+)/i);
                if (match && match[1]) {
                  logoUrl = match[1].trim();
                  break;
                }
              }
            }
          } else if (typeof bimiData === "object" && bimiData !== null) {
            logoUrl = bimiData.l || bimiData.logo || "";
          } else if (typeof bimiData === "string") {
            const match = bimiData.match(/l=([^;]+)/i);
            if (match && match[1]) {
              logoUrl = match[1].trim();
            }
          }
        }

        return (
          <div className="bg-slate-50 dark:bg-secondary/50 p-4 rounded-lg">
            <div className="text-sm text-slate-600 dark:text-slate-300">
              {!bimiData ? (
                `No ${record.type} records found`
              ) : (
                <div>
                  <pre className="whitespace-pre-wrap  text-slate-600 dark:text-slate-300">
                    {typeof bimiData === "object" && !Array.isArray(bimiData)
                      ? JSON.stringify(bimiData, null, 2)
                      : Array.isArray(bimiData)
                      ? bimiData.join("\n")
                      : bimiData.toString()}
                  </pre>

                  <EntryItem
                    label="Detected logo URL"
                    value={logoUrl || "None detected"}
                  />

                  {logoUrl && !logoUrl.toLowerCase().endsWith(".pem") && (
                    <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-yellow-700 dark:text-yellow-300">
                      <p>
                        Certificate is missing. Consider purchasing a VMC
                        certificate for stronger brand validation and
                        protection.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      }

      case "MTA-STS": {
        return (
          <div className="bg-slate-50 dark:bg-secondary/50 p-4 rounded-lg">
            <div className="text-sm  text-slate-600 dark:text-slate-300">
              {(() => {
                const recordData =
                  record.record?.record || record.record?.records;

                if (!recordData) {
                  return `No ${record.type} records found`;
                }

                if (Array.isArray(recordData)) {
                  return recordData.join(", ");
                }

                return typeof recordData === "object"
                  ? JSON.stringify(recordData, null, 2)
                  : recordData.toString();
              })()}
            </div>
            {record.record?.valid ? (
              <div className="mt-4 bg-slate-50 dark:bg-secondary/50 rounded-lg">
                <div className="text-sm text-slate-600 dark:text-slate-300">
                  Policy Document - https://{recordsData?.domain}
                  /.well-known/mta-sts.txt
                </div>
              </div>
            ) : null}
          </div>
        );
      }

      default:
        return (
          <div className="bg-slate-50 dark:bg-secondary/50 p-4 rounded-lg">
            <div className="text-sm text-slate-600 dark:text-slate-300 break-all">
              {(() => {
                const recordData =
                  record.record?.record || record.record?.records;

                if (!recordData) {
                  return `No ${record.type} records found`;
                }

                if (Array.isArray(recordData)) {
                  return recordData.join(", ");
                }

                return typeof recordData === "object"
                  ? JSON.stringify(recordData, null, 2)
                  : recordData.toString();
              })()}
            </div>
          </div>
        );
    }
  };

  return <div className="">{renderSubRecords()}</div>;
};

const EmailHealthRecords = () => {
  const { recordsData, state, toggleCollapseAll, sortedRecords, setState } =
    useEmailHealthReport();

  return (
    <div>
      <div className="mb-2 flex justify-between items-center">
        <h2 className="text-lg font-semibold">Domain Health Test Results</h2>
        <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (!state.isAllExpanded) {
                toggleCollapseAll("expand");
              } else {
                toggleCollapseAll("collapse");
              }
            }}
            className="text-xs"
          >
            {!state.isAllExpanded ? (
              <>
                <ChevronDownIcon className="h-4 w-4 mr-1" />
                Expand All
              </>
            ) : (
              <>
                <ChevronUpIcon className="h-4 w-4 mr-1" />
                Collapse All
              </>
            )}
          </Button>
        </div>
      </div>

      <div className=" mb-6">
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sortedRecords.map((record: any, index: number) => (
              <Collapsible
                key={index}
                open={state.openSections[index]}
                onOpenChange={(isOpen) => {
                  setState({
                    ...state,
                    openSections: {
                      ...state.openSections,
                      [index]: isOpen,
                    },
                  });
                }}
              >
                <Card className="overflow-hidden shadow-none">
                  <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-3 [&[data-state=open]>svg]:rotate-180">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          record.partialValid === "valid" &&
                          record.status !== "valid"
                            ? "bg-orange-500"
                            : record.status === "valid"
                            ? "bg-green-500"
                            : "bg-red-500"
                        }`}
                      />
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{record.type}</h3>
                          <StyledTooltip description={getTooltip(record.type)}>
                            <Info className="w-3 h-3 text-muted-foreground" />
                          </StyledTooltip>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 whitespace-normal break-words break-all">
                          {record.description ||
                            (record.status === "valid"
                              ? "Record found"
                              : "No record found")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <MoreInfoButton slug={record.type} className="shrink-0" />
                      <span
                        className={`${
                          record?.result || record.status === "valid"
                            ? "text-green-500"
                            : "text-red-500"
                        }`}
                      >
                        {record?.result || record.status === "valid" ? (
                          <CheckCircle2 className="w-4 h-4" />
                        ) : (
                          <XCircle className="w-4 h-4" />
                        )}
                      </span>
                      <ChevronDown className="w-4 h-4 transition-transform duration-200" />
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="p-4 text-sm border-t">
                      <RecordDisplay
                        record={record}
                        recordsData={recordsData}
                        isPrinting={state.isPrinting}
                      />
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const EntryItem = ({
  label,
  value,
}: {
  label: string;
  value: string | React.ReactNode;
}) => {
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className="text-slate-500 dark:text-slate-400 whitespace-nowrap">
        {label}:
      </span>
      <span className="text-slate-700 dark:text-slate-300 break-all">
        {value}
      </span>
    </div>
  );
};

export default EmailHealthRecords;
